/**
 * Which pages an installed app may show — its *reach*.
 *
 * Two questions used to share one answer, manifest scope. They no longer do:
 * the rail is the app's membership (`APP_NAV`), and reach is the manifest scope
 * PLUS every member's subtree. Membership alone would hide Labels and Spam
 * inside Inbox (in scope, not rail members) and drop a `/meals/foods` side
 * pane inside Food the moment it navigated (`useSecondaryPaneVisible`
 * re-evaluates on every render). Scope alone could never reach Notes from
 * Study.
 *
 * Consequence worth knowing: a hard reload on a member outside the manifest
 * scope shows the browser strip. Inherent to manifests; not this module's problem.
 *
 * Spec: docs/plans/2026-09-16-pwa-app-nav-design.md §4.2.
 */
import { APP_HOME_PATHS } from '@shared/app-identity/appHomePaths';
import { APP_NAV } from '@shared/app-identity/appNav';
import { destinationById, isAtOrUnder } from '@shared/navigation/destinations';

import { splitFullPath } from './helpers/splitFullPath';

import type { AppSlug } from '@shared/app-identity/installedApp';

/**
 * True when `tabPath` is a page `slug` may show. `null` — a browser tab or
 * root Conway — owns the whole origin, so everything belongs.
 *
 * Parses with `splitFullPath` rather than comparing strings: `tab.path` carries
 * an optional search and hash, and `URL` parsing also normalizes dot segments
 * so `/study/../attention` cannot pass a prefix test and then resolve away.
 * Tests each member's base by prefix rather than resolving the path to its
 * longest catalog match, so a member's subtree counts even where a deeper
 * destination exists there.
 */
export function belongsToApp(tabPath: string, slug: AppSlug | null): boolean {
  if (slug === null) return true;
  const { pathname } = splitFullPath(tabPath);
  if (isAtOrUnder(pathname, APP_HOME_PATHS[slug])) return true;
  return APP_NAV[slug].some(id => {
    const dest = destinationById(id);
    return dest !== undefined && isAtOrUnder(pathname, dest.activeBasePath ?? dest.path);
  });
}
